import type { AxiosRequestConfig, Canceler } from 'axios'
import axios from 'axios'

/**
 * @description Cancel duplicate requests
 */
export class AxiosCanceler {
  // Store the identifier and cancel function for each request
  pendingMap: Map<string, Canceler>

  constructor() {
    this.pendingMap = new Map<string, Canceler>()
  }

  /**
   * @description Add request
   * @param {Object} config
   */
  addPending(config: AxiosRequestConfig) {
    if (!config) {
      return
    }
    // Remove duplicate request first
    this.removePending(config)
    const url = this.getPendingUrl(config)

    // Only create cancel token if it doesn't exist
    if (!config.cancelToken) {
      let cancel: Canceler | undefined
      config.cancelToken = new axios.CancelToken(c => {
        cancel = c
      })
      // CancelToken constructor calls the executor synchronously, so cancel is assigned immediately
      // Store cancel function for this request
      if (cancel) {
        this.pendingMap.set(url, cancel)
      }
    }
  }

  /**
   * @description Remove request
   * @param {Object} config
   */
  removePending(config: AxiosRequestConfig | undefined) {
    if (!config) {
      return
    }
    const url = this.getPendingUrl(config)
    if (this.pendingMap.has(url)) {
      // If the current request identifier exists in pending, cancel the current request and remove it
      const cancel = this.pendingMap.get(url)
      cancel && cancel(url)
      this.pendingMap.delete(url)
    }
  }

  /**
   * @description Clear all pending requests
   */
  removeAllPending() {
    this.pendingMap.forEach(cancel => {
      cancel && cancel()
    })
    this.pendingMap.clear()
  }

  /**
   * @description Reset
   */
  reset(): void {
    this.pendingMap = new Map<string, Canceler>()
  }

  /**
   * @description Generate unique key for each request
   * @param {Object} config
   * @returns {string}
   */
  private getPendingUrl(config: AxiosRequestConfig): string {
    if (!config) {
      return ''
    }
    try {
      const params = config.params ? JSON.stringify(config.params) : ''
      const data = config.data ? JSON.stringify(config.data) : ''
      const method = config.method || 'GET'
      const url = config.url || ''
      return [method, url, params, data].join('&')
    } catch (error) {
      // Fallback if JSON.stringify fails (e.g., circular reference)
      const method = config.method || 'GET'
      const url = config.url || ''
      return `${method}&${url}&${String(config.params)}&${String(config.data)}`
    }
  }
}
